import React, { useState, useEffect } from "react";
import { Plus, BookOpen, Target, Users, Settings, Filter } from "lucide-react";
import toast from "react-hot-toast";

import {
  Course,
  CreateCourseRequest,
  UpdateCourseRequest,
  CourseFilter,
} from "../types/course";
import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  getCourseStats,
  bulkDeleteCourses,
} from "../services/courses";
import CoursesTable from "../components/tables/CoursesTable";
import CourseForm from "../components/forms/CourseForm";

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | undefined>(
    undefined
  );
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeCourses: 0,
    inactiveCourses: 0,
    averageCredits: 0,
  });
  const [filters, setFilters] = useState<CourseFilter>({
    page: 1,
    limit: 20,
    isActive: true,
  });

  useEffect(() => {
    loadCourses();
    loadStats();
  }, [filters]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await getCourses(filters);
      setCourses(response.data);
    } catch (error) {
      toast.error("Failed to load courses");
      setCourses([]); // Fallback for demo
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getCourseStats();
      setStats({
        totalCourses: response.data.totalCourses,
        activeCourses: response.data.activeCourses,
        inactiveCourses: response.data.inactiveCourses,
        averageCredits: response.data.averageCredits,
      });
    } catch (error) {
      // Use demo data if API fails
      setStats({
        totalCourses: 0,
        activeCourses: 0,
        inactiveCourses: 0,
        averageCredits: 0,
      });
    }
  };

  const handleCreateCourse = async (
    data: CreateCourseRequest | UpdateCourseRequest
  ) => {
    await createCourse(data as CreateCourseRequest);
    loadCourses();
    loadStats();
    setShowForm(false);
  };

  const handleUpdateCourse = async (
    data: CreateCourseRequest | UpdateCourseRequest
  ) => {
    if (editingCourse) {
      await updateCourse(editingCourse._id, data as UpdateCourseRequest);
      loadCourses();
      loadStats();
      setEditingCourse(undefined);
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      try {
        await deleteCourse(courseId);
        toast.success("Course deleted successfully");
        loadCourses();
        loadStats();
      } catch (error) {
        toast.error("Failed to delete course");
      }
    }
  };

  const handleBulkDelete = async (courseIds: string[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${courseIds.length} course(s)?`
      )
    ) {
      try {
        await bulkDeleteCourses(courseIds);
        toast.success("Courses deleted successfully");
        loadCourses();
        loadStats();
      } catch (error) {
        toast.error("Failed to delete courses");
      }
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCourse(undefined);
  };

  if (loading && courses.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-8">
        <div className="animate-pulse space-y-8 w-full max-w-7xl">
          <div className="h-12 bg-white/40 backdrop-blur-sm rounded-2xl w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-32 bg-white/40 backdrop-blur-sm rounded-2xl"
              ></div>
            ))}
          </div>
          <div className="h-96 bg-white/40 backdrop-blur-sm rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-8">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-3 rounded-2xl shadow-lg">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900">
                  Course Management
                </h1>
              </div>
              <p className="text-slate-600 ml-[60px]">
                Manage academic courses and curriculum
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="bg-white border-2 border-slate-200 hover:border-emerald-300 text-slate-700 px-6 py-3 rounded-xl font-bold flex items-center gap-3 hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-5 w-5 text-emerald-500" />
              Add Course
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Total Courses
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.totalCourses}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-4 rounded-2xl shadow-lg">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Active Courses
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.activeCourses}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-400 to-green-500 p-4 rounded-2xl shadow-lg">
                <Target className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Inactive Courses
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.inactiveCourses}
                </p>
              </div>
              <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-4 rounded-2xl shadow-lg">
                <Users className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <input
                type="text"
                placeholder="Search courses..."
                className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-medium text-slate-900 placeholder-slate-400"
                value={filters.search || ""}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </div>

            <div className="form-control">
              <select
                className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl px-4 py-3 font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                value={filters.level || ""}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    level: (e.target.value as any) || undefined,
                  }))
                }
              >
                <option value="">All Levels</option>
                <option value="Begginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div className="form-control">
              <select
                className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl px-4 py-3 font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                value={filters.category || ""}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    category: (e.target.value as any) || undefined,
                  }))
                }
              >
                <option value="">All Categories</option>
                <option value="core">Core</option>
                <option value="elective">Elective</option>
                <option value="practical">Practical</option>
                <option value="theory">Theory</option>
              </select>
            </div>

            <div className="form-control">
              <select
                className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl px-4 py-3 font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                value={filters.isActive?.toString() || ""}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    isActive: e.target.value
                      ? e.target.value === "true"
                      : undefined,
                  }))
                }
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <button
              onClick={() => setFilters({ page: 1, limit: 20, isActive: true })}
              className="bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 px-4 py-3 rounded-xl font-bold flex items-center gap-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Filter className="h-5 w-5 text-slate-500" />
              Reset
            </button>
          </div>
        </div>

        {/* Courses Table */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
          <CoursesTable
            courses={courses}
            loading={loading}
            onEdit={handleEditCourse}
            onDelete={handleDeleteCourse}
            onBulkDelete={handleBulkDelete}
          />
        </div>

        {/* Course Form Modal */}
        {(showForm || editingCourse) && (
          <CourseForm
            course={editingCourse}
            onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse}
            onClose={handleCloseForm}
          />
        )}
      </div>
    </div>
  );
};

export default Courses;
